// 같은 출생 조건·같은 시드로 산 두 인생을 나란히 놓는 계산. 기록에서 구할 수 있는 것만 담고 원인을 단정하지 않는다.
// 수명은 선택이 바꾸지 못하므로 비교하지 않는다. 직업은 선택보다 운에 좌우되는 편이라 같으면 "같은 직업"이라고만 말한다.
import { GROUPS, GROUP_LABEL } from './rules.js';
import { compareLives } from './summary.js';
import { buildResultCard } from './resultCard.js';
import { jobTitle } from './engine.js';
import { runIdentity, sameStart } from './storage.js';

/** 비교 대상 후보: 같은 출생 조건으로 보관된 다른 완료 기록. 기본값은 직전 비교 기록, 없으면 가장 최근 것. */
export function comparisonCandidates(run, lives) {
  return (lives ?? []).filter((life) => runIdentity(life) !== runIdentity(run) && sameStart(run, life));
}
export function pickPrevious(run, lives, selectedId = '') {
  const candidates = comparisonCandidates(run, lives);
  const wanted = selectedId || run.comparisonRunId || '';
  return candidates.find((life) => runIdentity(life) === wanted) ?? candidates.at(-1) ?? null;
}

/** 사건 선택 중 자동 결정(기질 따름)이 차지한 비율로 그 인생이 어떻게 살았는지 이름 붙인다. 선택한 사건이 없으면 null. */
export function lifeMode(run) {
  const picks = (run.log ?? []).filter((entry) => entry.kind === 'event');
  if (!picks.length) return null;
  const auto = picks.filter((entry) => entry.autoReason).length, share = auto / picks.length;
  return { auto, total: picks.length, label: share >= 0.8 ? '기질대로' : share >= 0.5 ? '기질+직접 선택' : '내 선택' };
}

const side = (label, run) => {
  const card = buildResultCard(run);
  return {
    label, mode: lifeMode(run), endAge: card.endAge, title: card.title,
    stats: GROUPS.map((group) => ({ group, label: GROUP_LABEL[group], value: Math.round(run.stats?.[group] ?? 0) })),
    job: run.job ? jobTitle(run.job) : null, jobName: run.job?.name ?? null, level: run.job?.level ?? 0,
    changes: Array.isArray(run.jobHistory) ? run.jobHistory.length : 0,
  };
};

const decisionLine = (items) => {
  const line = items.find((text) => !text.startsWith('시간:'));
  return line ? line.replace(/^[^:]+:\s*/, '') : null;   // "사건: 제목 → 선택" 에서 앞 분류를 뗀다
};

/** { sameRules, firstAge, headline, previous, current, same: {job, stats[]} } — 비교할 기록이 없으면 null */
export function buildCompareCards(run, previous) {
  const cmp = compareLives(run, previous);
  if (!cmp) return null;
  const prev = side('이전', previous), cur = side('이번', run);
  const diff = cmp.firstDifference;
  let headline, fork = null;
  if (!diff) headline = prev.mode?.label === '기질대로' && cur.mode?.label === '기질대로'
    ? '두 번 모두 기질대로 살아서 같은 인생이 됐어요. 이번엔 직접 골라 보세요.'
    : '두 번 모두 같은 선택을 해서 같은 인생이 됐어요. 같은 운에서는 같은 선택이 같은 인생을 만들어요. 이번엔 다르게 골라 보세요.';
  else {
    const before = decisionLine(diff.previous), now = decisionLine(diff.current);
    if (before && now) { headline = `${diff.age}세에 처음 갈라졌어요.`; fork = { previous: before, current: now }; }
    else headline = `${diff.age}세에 처음 갈라졌어요. 시간을 나눠 쓴 방식이 달랐어요.`;
  }
  return {
    sameRules: cmp.sameRules, firstAge: diff?.age ?? null, headline, fork, previous: prev, current: cur,
    same: { job: !!prev.jobName && prev.jobName === cur.jobName, level: prev.level === cur.level },
    deltas: GROUPS.map((group, i) => ({ group, label: GROUP_LABEL[group], diff: cur.stats[i].value - prev.stats[i].value })),
  };
}
